
import { supabase } from "@/integrations/supabase/client";
import { detectSeasonalIntent, getSeasonalRecommendations } from '../../../supabase/functions/ai-vacation-search/seasonalIntelligence';
import { validateVacationData } from './utils/SearchUtils';

export const performSeasonalSearch = async (query: string) => {
  try {
    console.log('🌤️ === PERFORMING SEASONAL SEARCH ===');
    console.log('Original query:', `"${query}"`);
    
    // Detect seasonal intent from the query
    const seasonalIntent = detectSeasonalIntent(query);
    
    if (!seasonalIntent.isSeasonalQuery) {
      console.log('❌ No seasonal intent detected');
      return [];
    }
    
    console.log('✅ Detected seasonal intent:', seasonalIntent);
    
    // Get seasonal recommendations
    let recommendations;
    if (seasonalIntent.month && seasonalIntent.weatherPreference) {
      recommendations = getSeasonalRecommendations(
        seasonalIntent.month, 
        seasonalIntent.weatherPreference,
        seasonalIntent.activities,
        seasonalIntent.geographicRestrictions
      );
    } else if (seasonalIntent.month) {
      // Default to warm weather if month is specified but no weather preference
      recommendations = getSeasonalRecommendations(
        seasonalIntent.month, 
        'warm',
        seasonalIntent.activities,
        seasonalIntent.geographicRestrictions
      );
    } else if (seasonalIntent.weatherPreference) {
      // Use current month if weather preference but no specific month
      const currentMonth = new Date().toLocaleDateString('nl-NL', { month: 'long' });
      recommendations = getSeasonalRecommendations(
        currentMonth.toLowerCase(), 
        seasonalIntent.weatherPreference,
        seasonalIntent.activities,
        seasonalIntent.geographicRestrictions
      );
    }
    
    if (!recommendations || recommendations.countries.length === 0) {
      console.log('❌ No seasonal recommendations found for criteria');
      return [];
    }
    
    console.log('🎯 Seasonal recommendations:', recommendations);
    
    // Build query to find vacations in recommended countries and regions
    let dbQuery = supabase
      .from('Complete feed')
      .select('*')
      .not('imageURL', 'is', null)
      .not('name', 'is', null);
    
    // Create filter for recommended countries and regions
    const countryFilters = recommendations.countries.map(country => 
      `country.eq.${country}`
    );
    
    const regionFilters = recommendations.regions.map(region => 
      `region.ilike.%${region}%`
    );
    
    // Combine country and region filters with OR logic
    const allFilters = [...countryFilters, ...regionFilters];
    
    if (allFilters.length > 0) {
      const orFilter = allFilters.join(',');
      dbQuery = dbQuery.or(orFilter);
      console.log('🔍 Applied seasonal location filters:', orFilter);
    }
    
    // Add activity-based filtering if specified
    if (seasonalIntent.activities?.includes('beach')) {
      dbQuery = dbQuery.or('facilities.ilike.%strand%,facilities.ilike.%beach%,facilities.ilike.%zee%,description.ilike.%strand%,description.ilike.%beach%');
      console.log('🏖️ Added beach activity filter');
    }
    
    if (seasonalIntent.activities?.includes('winter')) {
      dbQuery = dbQuery.or('typeVakantie.ilike.%wintersport%,facilities.ilike.%ski%,description.ilike.%ski%,description.ilike.%wintersport%');
      console.log('⛷️ Added winter sports filter');
    }
    
    if (seasonalIntent.activities?.includes('party')) {
      dbQuery = dbQuery.or('typeVakantie.ilike.%jongerenreizen%,specialCategories.ilike.%party%,description.ilike.%nachtleven%,description.ilike.%uitgaan%');
      console.log('🎉 Added party/nightlife filter');
    }
    
    // Add seasonal boost based on current period
    const currentMonth = new Date().getMonth() + 1;
    console.log(`📅 Current month: ${currentMonth}, boosting seasonal relevance`);
    
    const { data, error } = await dbQuery.limit(100);
    
    if (error) {
      console.error('❌ Seasonal search error:', error);
      throw error;
    }
    
    console.log(`🌤️ Seasonal search found ${data?.length || 0} results for "${query}"`);
    console.log(`💡 Recommendation: ${recommendations.reason}`);
    console.log(`🌡️ Expected temperature: ${recommendations.temperature}`);
    
    // Add seasonal context to results
    const resultsWithContext = validateVacationData(data).map(vacation => ({
      ...vacation,
      seasonalContext: {
        reason: recommendations.reason,
        temperature: recommendations.temperature,
        isSeasonalMatch: true
      }
    }));
    
    return resultsWithContext;
  } catch (error: any) {
    console.error('💥 Seasonal search error:', error);
    return [];
  }
};
